import { useEffect, useRef, useState } from "react";
import { useInView } from "framer-motion";
import { MapPin, Target, Handshake, Award } from "lucide-react";
import { Reveal } from "./Reveal";
import { STATS } from "../data/stats";

// Browser headless / crawler (es. snapshot per Googlebot): niente animazione, valore finale subito.
const isCrawler = () =>
    typeof navigator !== "undefined" &&
    (navigator.webdriver || /bot|crawl|spider|headless|prerender|lighthouse/i.test(navigator.userAgent));

// Il DOM contiene fin da subito il valore finale; l'animazione da 0 parte solo per i visitatori reali.
const Counter = ({ to, suffix = "" }) => {
    const ref = useRef(null);
    const inView = useInView(ref, { once: true, margin: "-40px" });
    const [val, setVal] = useState(to);

    useEffect(() => {
        if (!inView || isCrawler()) return;
        const start = performance.now();
        const dur = 1500;
        const tick = (t) => {
            const p = Math.min((t - start) / dur, 1);
            setVal(Math.round(to * (1 - Math.pow(1 - p, 3))));
            if (p < 1) requestAnimationFrame(tick);
        };
        requestAnimationFrame(tick);
    }, [inView, to]);

    return (
        <span ref={ref}>
            {val}
            {suffix}
        </span>
    );
};

const VALUES = [
    {
        icon: Target,
        title: "Competenza tecnica",
        desc: "Tecnici specializzati e formati direttamente dai costruttori.",
    },
    {
        icon: Handshake,
        title: "Vicinanza al cliente",
        desc: "Un unico referente, dal progetto all'assistenza post-vendita.",
    },
    {
        icon: Award,
        title: "Qualità certificata",
        desc: "Solo marchi leader e ricambi originali garantiti.",
    },
];

export const ChiSiamo = () => (
    <section
        id="chi-siamo"
        data-testid="chi-siamo-section"
        className="bg-[#F2F5F9] py-20 lg:py-28"
    >
        <div className="mx-auto max-w-[1440px] px-4 sm:px-8 lg:px-16">
            <div className="grid items-center gap-14 lg:grid-cols-12">
                <div className="relative lg:col-span-5">
                    <Reveal>
                        <div className="relative">
                            <div
                                aria-hidden="true"
                                className="absolute -inset-3 rounded-[2.4rem] border-2 border-[#0B1F3A]/10"
                            />
                            <img
                                src="/brands/ftm-targa.jpg"
                                alt="Targa FTM Group S.r.l."
                                loading="lazy"
                                className="w-full rounded-[2rem] object-cover shadow-[0_32px_80px_-24px_rgba(11,31,58,0.35)]"
                                data-testid="chi-siamo-image"
                            />
                            <img
                                src="/christ/cadis.jpg"
                                alt="Portale di lavaggio Christ CADIS"
                                loading="lazy"
                                className="mt-5 h-56 w-full rounded-[2rem] object-cover shadow-[0_24px_60px_-24px_rgba(11,31,58,0.3)]"
                                data-testid="chi-siamo-image-secondary"
                            />
                            <div
                                className="absolute -bottom-6 right-4 rounded-2xl bg-[#0B1F3A] px-6 py-4 shadow-xl sm:right-8"
                                data-testid="chi-siamo-location-card"
                            >
                                <div className="flex items-center gap-2 text-[#E30613]">
                                    <MapPin className="h-4 w-4" />
                                    <span className="text-[10px] font-bold uppercase tracking-[0.18em]">
                                        La nostra sede
                                    </span>
                                </div>
                                <div className="font-display mt-1 text-base font-bold text-white">
                                    Castrolibero · Cosenza
                                </div>
                            </div>
                        </div>
                    </Reveal>
                </div>

                <div className="lg:col-span-7">
                    <Reveal>
                        <span className="text-xs font-bold uppercase tracking-[0.22em] text-[#E30613]">
                            Chi siamo
                        </span>
                    </Reveal>
                    <Reveal delay={0.1}>
                        <h2 className="font-display mt-4 text-3xl font-extrabold tracking-tight text-[#0B1F3A] sm:text-4xl">
                            Il partner tecnico delle imprese del Sud.
                        </h2>
                    </Reveal>
                    <Reveal delay={0.18}>
                        <p className="mt-6 text-base leading-relaxed text-[#475569] sm:text-lg">
                            FTM Group S.r.l. nasce a Castrolibero, nel cuore della Calabria, con una
                            missione precisa: portare alle aziende del Sud Italia le migliori
                            tecnologie meccaniche e industriali, con lo stesso livello di servizio
                            dei grandi centri. Oggi siamo il punto di riferimento per officine,
                            concessionarie e industrie del Sud Italia e per le stazioni di servizio
                            della Calabria, dove siamo concessionari ufficiali Christ Wash Systems.
                        </p>
                    </Reveal>

                    <div className="mt-10 grid gap-6 sm:grid-cols-3" data-testid="chi-siamo-values">
                        {VALUES.map((v, i) => (
                            <Reveal key={v.title} delay={0.1 + i * 0.08}>
                                <div className="h-full rounded-2xl border border-slate-200 bg-white p-5">
                                    <v.icon className="h-6 w-6 text-[#E30613]" strokeWidth={1.8} />
                                    <div className="font-display mt-3 text-sm font-bold text-[#0B1F3A]">
                                        {v.title}
                                    </div>
                                    <div className="mt-1.5 text-xs leading-relaxed text-[#4F5D70]">
                                        {v.desc}
                                    </div>
                                </div>
                            </Reveal>
                        ))}
                    </div>

                    <Reveal delay={0.25}>
                        <div
                            className="mt-10 grid grid-cols-2 gap-6 rounded-3xl bg-[#0B1F3A] p-8 sm:grid-cols-4"
                            data-testid="chi-siamo-stats"
                        >
                            {STATS.map((s, i) => (
                                <div key={s.label} data-testid={`chi-siamo-stat-${i}`}>
                                    <div
                                        className={`font-display whitespace-nowrap text-2xl font-extrabold sm:text-3xl ${s.accent ? "text-[#E30613]" : "text-white"}`}
                                    >
                                        {s.countTo ? <Counter to={s.countTo} suffix={s.suffix} /> : s.value}
                                    </div>
                                    <div className="mt-1 text-xs font-medium uppercase tracking-wider text-white/60">
                                        {s.label}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </Reveal>
                </div>
            </div>
        </div>
    </section>
);
